import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import {
  Box, Typography, TextField, Button, Alert, Paper, CircularProgress,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { CREATE_SUGGESTION } from '@/graphql/mutations'

const MAX_CHARS = 1000

export const SuggestionsPage: React.FC = () => {
  const { t } = useTranslation()
  const [message, setMessage] = useState('')
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  const [createSuggestion, { loading }] = useMutation(CREATE_SUGGESTION)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess(false)

    if (!message.trim()) {
      setError(t('suggestions.errorEmpty'))
      return
    }

    try {
      await createSuggestion({ variables: { input: { message: message.trim() } } })
      setMessage('')
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('suggestions.errorGeneric'))
    }
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        {t('suggestions.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('suggestions.description')}
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          {t('suggestions.success')}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper elevation={1} sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            multiline
            rows={5}
            fullWidth
            value={message}
            onChange={(e) => {
              if (e.target.value.length <= MAX_CHARS) setMessage(e.target.value)
            }}
            placeholder={t('suggestions.placeholder')}
            inputProps={{ maxLength: MAX_CHARS }}
            disabled={loading}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {message.length}/{MAX_CHARS}
            </Typography>
            <Button type="submit" variant="contained" disabled={loading || !message.trim()}>
              {loading ? <CircularProgress size={20} /> : t('suggestions.submit')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
